<template>
  <figure class="viz-card activity-card">
    <figcaption class="viz-header">
      <h3>{{ title }}</h3>
      <span v-if="note" class="viz-note">{{ note }}</span>
    </figcaption>

    <div v-if="loading" class="viz-empty">Loading…</div>
    <div v-else-if="!items.length" class="viz-empty">{{ emptyText }}</div>
    <ul v-else class="activity-list">
      <li
        v-for="item in items"
        :key="item.id"
        class="activity-row"
        :class="{ clickable: !!item.clickAction }"
        :title="item.subtext ? `${item.text} — ${item.subtext}` : item.text"
        @click="handleClick(item)"
      >
        <span class="activity-icon" :class="item.tone ? `tone-${item.tone}` : ''">
          <font-awesome-icon :icon="item.icon" />
        </span>
        <span class="activity-body">
          <span class="activity-text">{{ item.text }}</span>
          <span v-if="item.subtext" class="activity-subtext">{{ item.subtext }}</span>
        </span>
        <span class="activity-meta">{{ item.meta }}</span>
      </li>
    </ul>
  </figure>
</template>

<script>
export default {
  name: 'RecentActivityFeed',
  props: {
    title: { type: String, required: true },
    note: { type: String, default: '' },
    // [{ id, icon, tone?, text, subtext?, meta, clickAction? }] - tone: 'good' | 'warning' | 'critical' | 'accent'.
    // `text` is the short, scannable headline (task code, trainee name); `subtext`
    // is longer supporting context (product/session name) - truncated with an
    // ellipsis and surfaced in full via the row's title tooltip on hover.
    // clickAction, when present, is emitted verbatim on 'item-click'.
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'Nothing to show yet.' },
  },
  emits: ['item-click'],
  methods: {
    handleClick(item) {
      if (item.clickAction) this.$emit('item-click', item.clickAction);
    },
  },
};
</script>

<style scoped>
.viz-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin: 0;
  flex: 1 1 420px;
  min-width: 0; /* let this shrink below its content's intrinsic width instead of forcing the row wider / wrapping a sibling out of view */
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: box-shadow 0.15s ease;
  /* App.vue sets a global `#app { text-align: center }` - override it here
     so the feed's title, rows and truncating text stay left-aligned. */
  text-align: left;
}

.viz-card:hover {
  box-shadow: 0 6px 16px rgba(11, 11, 11, 0.07);
}

.viz-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gridline);
}

.viz-header h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.viz-note {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.viz-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 24px 0;
  text-align: center;
}

.activity-card {
  max-height: 342px;
  overflow: hidden;
}

.activity-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 268px;
  overflow-y: auto;
}

.activity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 2px;
  border-bottom: 1px solid var(--gridline);
}

.activity-row:last-child {
  border-bottom: none;
}

.activity-row.clickable {
  cursor: pointer;
}

.activity-row.clickable:hover {
  background: var(--page-plane);
}

.activity-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  background: var(--series-1-tint);
  color: var(--series-1);
}

.activity-icon.tone-good {
  background: rgba(12, 163, 12, 0.12);
  color: var(--status-good);
}

.activity-icon.tone-warning {
  background: rgba(250, 178, 25, 0.16);
  color: #a06c00;
}

.activity-icon.tone-critical {
  background: rgba(208, 59, 59, 0.12);
  color: var(--status-critical);
}

.activity-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.activity-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-subtext {
  font-size: 11.5px;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-meta {
  flex-shrink: 0;
  font-size: 11.5px;
  color: var(--text-muted);
  white-space: nowrap;
}
</style>
